/**
 * client/src/types/lexistar-dispatch-board.ts
 *
 * API shapes of the LexiStar dispatch planning board (ST-00250): a day's order bubbles, the
 * trucks allocated to them (each allocation IS a Planned trip of Plan & Dispatch), the
 * absorption of each order and the rail of trucks (server/modules/lexistar/dispatchboard;
 * migration 077). The server copy is server/types/lexistar-dispatch-board.ts — keep the
 * two in step. Instants are ISO-8601 UTC; plan_date is a SAST calendar day and start_time a
 * SAST wall-clock time (HH:MM).
 */

export type DispatchShift = 'day' | 'night';
export type OrderStatus = 'open' | 'closed' | 'cancelled';
/** Where an allocation's tonnes estimate came from. */
export type TonnesBasis = 'truck' | 'lane' | 'order' | 'none';
export type TargetKind = 'tonnes' | 'loads';
/** under / on / over the target; none = nothing allocated yet (or no usable target). */
export type AbsorptionState = 'under' | 'on' | 'over' | 'none';

/** The realtime event the board emits on /tracking (tenant:{id}) after every change. */
export const DISPATCH_BOARD_EVENT = 'DISPATCH_BOARD.CHANGED';

export interface Absorption {
  /** tonnes when the order has a tonnes target, else loads, else null. */
  target_kind: TargetKind | null;
  target: number | null;
  /** Allocations that count (their trip is not cancelled / aborted). */
  allocated_loads: number;
  /** Sum of the counting allocations' estimated tonnes (allocations without an estimate add 0). */
  allocated_tonnes: number;
  /** Allocations that count but have no tonnes estimate (shown as "n loads unestimated"). */
  unestimated_loads: number;
  /** allocated / target (0..n), null without a target. */
  ratio: number | null;
  /** ratio × 100, rounded to a whole number. */
  pct: number | null;
  state: AbsorptionState;
  /** Target minus allocated, in the target's unit (negative = over). */
  remaining: number | null;
  /** The "on target" band: half a load's tonnes (tonnes targets), 0 for loads targets. */
  tolerance: number | null;
}

export interface BoardAllocation {
  id: string;
  order_id: string;
  trip_id: string;
  trip_reference: string | null;
  trip_stage: string | null;
  asset_id: string;
  registration: string | null;
  fleet_no: string | null;
  planned_tonnes: number | null;
  tonnes_basis: TonnesBasis;
  basis_loads: number | null;
  planned_start: string | null;
  allocated_at: string;
  allocated_by: string;
  released_at: string | null;
  released_by: string | null;
  /** Counts towards absorption: not released and its trip is not CANCELLED / ABORTED. */
  counts: boolean;
}

export interface BoardOrder {
  id: string;
  tenant_id: number;
  plan_date: string;
  shift: DispatchShift | null;
  start_time: string;
  order_ref: string;
  customer: string | null;
  material: string | null;
  lane_id: string;
  lane_name: string | null;
  from_location_id: number | null;
  to_location_id: number | null;
  from_name: string | null;
  to_name: string | null;
  route_id: string | null;
  route_code: string | null;
  target_tonnes: number | null;
  target_loads: number | null;
  tonnes_per_load: number | null;
  notes: string | null;
  status: OrderStatus;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string | null;
  /** Optimistic concurrency: send it back with every change / allocation / release. */
  version: number;
  allocations: BoardAllocation[];
  absorption: Absorption;
}

export type TruckAvailability = 'available' | 'planned_maintenance' | 'breakdown';

export interface TruckBooking {
  event_id: string;
  category: 'planned_maintenance' | 'breakdown';
  label: string;
  starts_at: string;
  expected_end_at: string | null;
}

export interface BoardTruck {
  asset_id: string;
  registration: string | null;
  fleet_no: string | null;
  asset_type: string;
  vehicle_class: string | null;
  /** available, or the open workshop / breakdown event's category. */
  availability: TruckAvailability;
  unavailable_label: string | null;
  unavailable_until: string | null;
  /** Workshop bookings (planned maintenance / breakdown) that touch the plan day, open or booked. */
  bookings: TruckBooking[];
  /** The truck's current trip (an active stage), else its next Planned trip, else null. */
  trip: { id: string; reference: string | null; stage: string; planned_start: string | null } | null;
  /** Loads this board (the day, every order) has on the truck that still count. */
  loads_on_board: number;
  tonnes_on_board: number;
  /** The truck's average net tonnes per load over the last 90 days (null = no history). */
  avg_tonnes: number | null;
  avg_loads: number;
}

export interface DispatchBoardResponse {
  date: string;
  shift: DispatchShift | null;
  now: string;
  tenant_ids: number[];
  orders: BoardOrder[];
  trucks: BoardTruck[];
}

export interface OrderInput {
  plan_date: string;
  shift?: DispatchShift | null;
  start_time?: string;
  order_ref: string;
  customer?: string | null;
  material?: string | null;
  lane_id: string;
  route_id?: string | null;
  target_tonnes?: number | null;
  target_loads?: number | null;
  tonnes_per_load?: number | null;
  notes?: string | null;
  /** Global admins only: the organisation the order is for. */
  tenant_id?: number;
}

export interface OrderPatch {
  version: number;
  shift?: DispatchShift | null;
  start_time?: string;
  order_ref?: string;
  customer?: string | null;
  material?: string | null;
  route_id?: string | null;
  target_tonnes?: number | null;
  target_loads?: number | null;
  tonnes_per_load?: number | null;
  notes?: string | null;
  status?: OrderStatus;
}

export interface AllocateInput {
  asset_id: string;
  version: number;
  /** Override the computed planned start (ISO instant). */
  planned_start?: string;
}

export interface ReleaseInput {
  version: number;
  reason?: string;
}

export interface AllocateResponse {
  order: BoardOrder;
  allocation: BoardAllocation;
  trip: { id: string; reference: string | null; stage: string };
  warnings: unknown[];
}

export interface ReleaseResponse {
  order: BoardOrder;
  allocation: BoardAllocation;
  trip: { id: string; reference: string | null; stage: string };
}

/** A 409 from the board: `code` says which rule, `current` the order as it is now (stale only). */
export interface BoardConflict {
  error: string;
  code: 'stale' | 'duplicate' | 'unavailable' | 'state';
  current?: BoardOrder;
}
